/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useState } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

type FilterQualityScorePopoverProps = {
  filterValue: { min: number, max: number };
  onApply: (obj: { min: number, max: number }) => void;
}

const FilterQualityScorePopover = (props: FilterQualityScorePopoverProps) => {
  const { filterValue, onApply } = props;
  const [minVal, setMinVal] = useState<number>(0);
  const [maxVal, setMaxVal] = useState<number>(0);
  const [rangeError, setRangeError] = useState<string>('');
  
  useEffect(() => {
    setMinVal(filterValue?.min || 0)
    setMaxVal(filterValue?.max || 0)
  }, [filterValue])
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="inline-block bg-transparent p-0 rounded-full h-5 w-5 border-0">
          <MdArrowDropDown className="inline-block h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-3" align='end'>
        <div className="flex flex-col gap-4 w-full">
          <div className='flex flex-col'>
            <div className="flex items-center gap-4">
              <div className='flex flex-col gap-2.5'>
                <label className='text-sm font-medium'>
                  Min value <span className='font-normal text-xs'>(%)</span>
                </label>
                <input 
                  type="number"
                  value={minVal}
                  min={0}
                  max={100}
                  className={`text-left h-9 text-sm border rounded-lg w-[100px] 
                    focus:ring-0 focus:outline-0 focus:border-[1.4px] ${
                      !rangeError
                        ? 'focus:border-secondary border-audio-timeline'
                        : 'focus:border-negative-red border-negative-red'
                    }`}
                  onChange={(e) => {
                    const val = e.target.value
                    if (!val && val !== '0') {
                      setRangeError('Required');
                    } else if (val !== "" && (Number(val) < 0 || Number(val) > 100)) {
                      setRangeError('Should be between 0 and 100');
                    } else if (Number(val) > maxVal) {
                      setRangeError('Max should be greater than min');
                    } else {
                      setRangeError('');
                    }
                    setMinVal(Number(val));
                  }}
                  onKeyDown={e => {
                    if (['e', 'E', '+', '-'].includes(e.key))
                      e.preventDefault();
                  }}
                />
              </div>
              <div className='flex flex-col gap-2.5'>
                <label className='text-sm font-medium'>
                  Max value <span className='font-normal text-xs'>(%)</span>
                </label>
                <input 
                  type="number"
                  value={maxVal}
                  min={0}
                  max={100}
                  className={`text-left h-9 text-sm border rounded-lg w-[100px] 
                    focus:ring-0 focus:outline-0 focus:border-[1.4px] ${
                      !rangeError
                        ? 'focus:border-secondary border-audio-timeline'
                        : 'focus:border-negative-red border-negative-red'
                    }`}
                  onChange={(e) => {
                    const val = e.target.value
                    if (!val && val !== '0') {
                      setRangeError('Required');
                    } else if (val !== "" && (Number(val) < 0 || Number(val) > 100)) {
                      setRangeError('Should be between 0 and 100');
                    } else if (Number(val) < minVal) {
                      setRangeError('Max should be greater than min');
                    } else {
                      setRangeError('');
                    }
                    setMaxVal(Number(val));
                  }}
                  onKeyDown={e => {
                    if (['e', 'E', '+', '-'].includes(e.key))
                      e.preventDefault();
                  }}
                />
              </div>
            </div>
            {rangeError && <div className='text-xxs text-negative-red mt-0.5'>{rangeError}</div>}  
          </div>
          <div className="flex items-center gap-3 w-full">
            <Button 
              variant='secondary' 
              className='bg-[#E1EBEF] hover:bg-[#E1EBEF] text-secondary rounded text-xs font-bold flex-1'
              onClick={() => {
                setMinVal(0);
                setMaxVal(0);
                setRangeError('');
                onApply({ min: 0, max: 0 })
              }}
            >
              Reset
            </Button>
            <Button 
              variant='secondary' 
              className='bg-secondary hover:bg-secondary text-white rounded text-xs font-bold flex-1'
              onClick={() => {
                if (!minVal && !maxVal) return;
                onApply({ min: minVal, max: maxVal })
              }}
              disabled={Boolean(rangeError)}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FilterQualityScorePopover;
